import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { clsx } from '@wonderful/wwc/dist/src/helpers/clsx';
import { domPurifyWithConfigs } from '@wonderful/wwc/dist/src/utils/domPurifyWithConfigs';
// Import Swiper
import { Swiper, SwiperSlide, type SwiperClass } from 'swiper/react';
import { swiperInit } from '@/helpers/swiper';
swiperInit();
import { isPreviewing } from '@builder.io/sdk-react';

import { BuilderEditingHelper } from '@wonderful/wwc/dist/src/common/BuilderEditingHelper/BuilderEditingHelper';
import { getBuilderPreviewProductsData } from '@/helpers/getBuilderProductData';
import { Breakpoints } from '@/helpers/constants';
import { ProductCarouselModule } from './ProductCarousel.types';

import styles from './ProductCarousel.module.scss';
import Section from '@/components/design/Section/Section';
import BuilderImage from '@/components/common/BuilderImage/BuilderImage';
import { SingleProductType } from '@/store/types';
import { useTranslation } from '@/app/i18n/TranslationContext';
import { LocaleEnum } from '@/helpers/enums';

import { useIsRtl } from '@/helpers/utils';

//PROBABLY WE SHOULD FETCH SHELL/NOSHELL INFO FROM BACKEND BUT UNTIL THEN WE CAN USE THIS FUNCTION
const noShellChecker = (url: string) => {
  return /-no-shell\/$/i.test(url);
};

function ProductCarousel(props: ProductCarouselModule) {
  const { eyebrow, title, body } = props;
  const [isInViewport, setIsInViewport] = useState(false);

  const [products, setProducts] = useState(props.products);

  const { locale } = useTranslation();

  useEffect(() => {
    async function setPreviewData() {
      const mergedProducts = await getBuilderPreviewProductsData(
        props.products,
        locale as LocaleEnum
      );
      mergedProducts && setProducts(mergedProducts);
    }

    isPreviewing() && setPreviewData();
  }, [props.products, locale]);

  const productDataExists =
    typeof products !== 'undefined' && products.length > 0;

  const onEnterViewport = () => {
    setIsInViewport(true);
  };

  if (!products) {
    return (
      <BuilderEditingHelper
        componentName="ProductCarousel"
        visible={!productDataExists}
      />
    );
  }

  return (
    <Section
      className={clsx(
        'container',
        styles.container,
        isInViewport ? styles.inViewport : ''
      )}
      viewportThreshold={0.3}
      onEnterViewport={onEnterViewport}
    >
      <div className={styles.titles}>
        <span>{eyebrow}</span>
        <h2>{title}</h2>
        {body && (
          <div
            dangerouslySetInnerHTML={{ __html: domPurifyWithConfigs(body) }}
          />
        )}
      </div>

      <ResponsiveSwiper items={products} />
    </Section>
  );
}

const ResponsiveSwiper = ({ items }: { items: SingleProductType[] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useTranslation();

  const isRtl = useIsRtl();

  return (
    <Swiper
      className={clsx(styles.swiper, 'swiper-no-swiping')}
      data-testid="product-carousel-swiper"
      centeredSlides
      loop={items.length >= 8} // MT-387: Visually the carousel works with this min on desktop
      resistance
      resistanceRatio={1}
      slidesPerView={3}
      // addIcons:false prevents swiper v12 from injecting its default SVG
      // arrows, leaving only the custom white arrows styled in the module SCSS.
      navigation={{ addIcons: false }}
      pagination={{
        clickable: true,
      }}
      noSwiping={false}
      shortSwipes={true}
      onSlideChange={(t: SwiperClass) => {
        setActiveIndex(t.realIndex);
      }}
      a11y={{
        enabled: true,
      }}
      breakpoints={{
        [Breakpoints.md]: {
          noSwiping: true,
        },
        [Breakpoints.lg]: {
          slidesPerView: 5,
        },
      }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      {items[activeIndex].product !== undefined && (
        <span id="ada-content" slot="container-start">
          {items[activeIndex].product.value?.data?.productDetailUrl !==
            undefined && (
            <Link
              href={items[activeIndex].product.value.data.productDetailUrl}
              aria-atomic="true"
              aria-live="polite"
            >
              <strong>
                {items[activeIndex].product.value?.data.name}
                {noShellChecker(
                  items[activeIndex].product.value?.data.productDetailUrl
                )
                  ? `- ${t('No Shells')}`
                  : ''}
              </strong>
            </Link>
          )}
        </span>
      )}

      {items?.map((item, index) => (
        <SwiperSlide
          aria-hidden="true"
          key={index}
          className={styles.swiperItem}
        >
          <Link
            href={
              item.product?.value?.data.productDetailUrl
                ? item.product?.value.data.productDetailUrl
                : ''
            }
            tabIndex={-1}
            aria-hidden="true"
          >
            <div className={styles.imageWrapper}>
              <BuilderImage
                imageSrc={item.product?.value?.data.image}
                mobileWidth={'132px'}
                tabletWidth={'222px'}
                alt={item.product?.value?.data.imageAltText || ''}
              />
              <div className={`${styles.swiperItemInfo} `}>
                <strong>{item.product?.value?.data.name}</strong>
              </div>
            </div>
          </Link>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default ProductCarousel;
